import { useEffect, useRef } from 'react';
import { useI18n, type MessageKey } from '../i18n';
import { Icon, Logo } from '../ui/Icon';

const PRINCIPLES: Array<[MessageKey, MessageKey]> = [
  ['about.p1title', 'about.p1'],
  ['about.p2title', 'about.p2'],
  ['about.p3title', 'about.p3'],
  ['about.p4title', 'about.p4'],
  ['about.p5title', 'about.p5'],
];

export function AboutModal({
  open,
  onClose,
  versions,
  onReport,
}: {
  open: boolean;
  onClose: () => void;
  versions: string;
  onReport: () => void;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      aria-labelledby="about-title"
      className="w-[min(36rem,calc(100vw-2rem))] overflow-hidden rounded-panel bg-surface p-0 text-fg shadow-soft-lg backdrop:bg-fg/40"
    >
      <div className="px-6 pb-4 pt-6">
        <div className="flex items-center gap-3">
          <Logo size={36} />
          <div className="min-w-0 flex-1">
            <h2 id="about-title" className="font-display text-2xl font-[440]">
              {t('about.title')}
            </h2>
            <p className="text-sm text-muted">{t('app.tagline')}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-pill p-1.5 text-muted hover:bg-fg/10 hover:text-fg" aria-label={t('about.close')}>
            <Icon name="x" />
          </button>
        </div>
      </div>
      <div className="rib-band" aria-hidden="true" />
      <div className="px-6 py-5">
        <p className="font-display text-md font-[380] leading-relaxed">{t('about.intro')}</p>
        <ol className="mt-4 space-y-3">
          {PRINCIPLES.map(([title, body], i) => (
            <li key={title} className="flex gap-3">
              <span className="font-display w-5 shrink-0 text-md text-subtle tabular">{i + 1}</span>
              <div className="text-sm">
                <div className="font-semibold">{t(title)}</div>
                <div className="text-muted">{t(body)}</div>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 rounded-card bg-sunken px-4 py-3">
          <button type="button" onClick={onReport} className="inline-flex items-center gap-2 text-sm font-semibold text-fg underline decoration-line-strong underline-offset-4 hover:decoration-fg" data-testid="about-report">
            <Icon name="flag" size={13} />
            {t('about.report')}
          </button>
          <p className="mt-1 text-2xs text-muted">{t('about.reportHint')}</p>
        </div>
        <div className="mt-5 flex items-center justify-between gap-3 text-2xs text-subtle">
          <span className="tabular">{versions}</span>
          <button type="button" onClick={onClose} className="btn-primary">
            {t('about.close')}
          </button>
        </div>
      </div>
    </dialog>
  );
}
